import { useCallback, useState } from "react";
import { t } from "ttag";

import { useDispatch } from "metabase/redux";
import { forgotPassword } from "metabase/redux/auth";
import type { Location } from "metabase/router";
import { useSetting } from "metabase/settings";
import { Button } from "metabase/ui";

import { AuthLayout } from "../AuthLayout";
import { ForgotPasswordForm } from "../ForgotPasswordForm";

import {
  InfoBody,
  InfoIcon,
  InfoIconContainer,
  InfoLink,
  InfoMessage,
} from "./ForgotPassword.styled";

type ViewType = "form" | "disabled" | "success";

interface ForgotPasswordProps {
  location?: Location;
}

export const ForgotPassword = ({
  location,
}: ForgotPasswordProps): JSX.Element => {
  const isEmailConfigured = useSetting("email-configured?");
  const isLdapEnabled = useSetting("ldap-enabled");
  const canResetPassword = isEmailConfigured && !isLdapEnabled;
  const initialEmail =
    new URLSearchParams(location?.search).get("email") ?? undefined;

  const [view, setView] = useState<ViewType>(
    canResetPassword ? "form" : "disabled",
  );
  const dispatch = useDispatch();

  const handleSubmit = useCallback(
    async (email: string) => {
      await dispatch(forgotPassword(email)).unwrap();
      setView("success");
    },
    [dispatch],
  );

  return (
    <AuthLayout>
      {view === "form" && (
        <ForgotPasswordForm
          initialEmail={initialEmail}
          onSubmit={handleSubmit}
        />
      )}
      {view === "success" && <ForgotPasswordSuccess />}
      {view === "disabled" && <ForgotPasswordDisabled />}
    </AuthLayout>
  );
};

const ForgotPasswordSuccess = (): JSX.Element => {
  return (
    <InfoBody>
      <InfoIconContainer>
        <InfoIcon name="check" />
      </InfoIconContainer>
      <InfoMessage>
        {t` If the email exists, we'll send instructions on how to reset your password.`}
      </InfoMessage>
      <Button
        variant="filled"
        component="a"
        href="/auth/login"
      >{t`Back to sign in`}</Button>
    </InfoBody>
  );
};

const ForgotPasswordDisabled = (): JSX.Element => {
  return (
    <InfoBody>
      <InfoMessage>
        {t`Please contact an administrator to have them reset your password.`}
      </InfoMessage>
      <InfoLink to="/auth/login">{t`Back to sign in`}</InfoLink>
    </InfoBody>
  );
};
